import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthProvider'
import { useSync } from '../contexts/SyncProvider'
import { getPlatformAPI } from '../lib/platform'
import { LogoScreen } from '../components/common/LogoScreen'
import { useWindowRevealHandoff, useWindowRevealOverlay } from '../hooks/useWindowRevealHold'
import { navigateToHome } from './navigateToHome'
import { useEffect } from 'react'

function MainTransition() {
  const navigate = useNavigate()

  useEffect(() => {
    navigateToHome(navigate)
  }, [navigate])

  return <LogoScreen variant="full" />
}

function OnboardingTransition() {
  const navigate = useNavigate()

  useEffect(() => {
    const triggerOnboardingTransition = async () => {
      if (window.api) {
        try {
          await window.api.invoke('transition-to-onboarding')
        } catch (error) {
          console.error('[OnboardingTransition] IPC call failed:', error)
          navigate('/onboarding', { replace: true })
        }
      } else {
        navigate('/onboarding', { replace: true })
      }
    }
    triggerOnboardingTransition()
  }, [navigate])

  return <LogoScreen variant="full" />
}

/**
 * Hält den Vorhang, bis die Seite darunter übernommen hat.
 *
 * Eigene Komponente und kein Hook in `ProtectedRoute` selbst: Er darf erst
 * anmelden, wenn die App WIRKLICH gerendert wird — also nach allen frühen
 * Rückgaben oben (Anmeldung lädt, Weiterleitungen). Ein Hook im Rumpf liefe
 * schon vorher.
 */
function RevealHandoff() {
  useWindowRevealHandoff()
  return null
}

export function ProtectedRoute({ children, curtain = true }: {
  children: React.ReactNode
  /**
   * **Ohne Vorhang** (`false`): kein Logo-Schirm beim Laden der Anmeldung und keiner über der Seite.
   * Für die durchsichtige Schnelleingabe — dort stand er als beiger Kasten mit Logo im Bild
   * (gemeldet 04.10.2026). Ihr Fenster erscheint erst mit der Karte.
   */
  curtain?: boolean
}) {
  const { isAuthenticated, loading } = useAuth()
  const { syncEntitlement, entitlementLoaded } = useSync()
  const location = useLocation()
  const loginMethod = localStorage.getItem('loginMethod')
  const revealHolding = useWindowRevealOverlay()
  // Web is a hard paywall: an authenticated user without a sync entitlement may
  // only reach /account. Electron is offline-capable, so it has no paywall.
  const isWeb = getPlatformAPI().platform === 'web'

  // Wait while the entitlement is still resolving for a web user, so we never
  // flicker an entitled user through the paywall.
  if (loading || (isWeb && isAuthenticated && !entitlementLoaded)) {
    return curtain ? <LogoScreen variant="full" /> : null
  }

  // Web hard paywall: an authenticated user without a sync entitlement can only
  // reach /account (booking/activation + profile). Everything else is gated.
  if (isWeb && isAuthenticated && !syncEntitlement) {
    return <Navigate to="/account" replace />
  }

  const isOfflineMode = loginMethod === 'offline'
  const offlineSetupCompleted = localStorage.getItem('offlineSetupCompleted') === 'true'

  // A completed offline setup grants access regardless of `loginMethod`. This
  // breaks an /onboarding ↔ "/" redirect loop: if `offlineSetupCompleted` is
  // true but `loginMethod` isn't 'offline' (a contradictory state that can
  // survive restarts / repeated testing), the old gate sent the user to "/",
  // where SetupRoute bounced them straight back to /onboarding — forever.
  const hasAccess = isAuthenticated || offlineSetupCompleted || (isOfflineMode && location.pathname !== '/home')

  if (!hasAccess) {
    return <Navigate to="/" replace />
  }

  if (isOfflineMode && !offlineSetupCompleted && location.pathname === '/home') {
    return <Navigate to="/setup-profile" replace />
  }

  const onboardingCompleted = localStorage.getItem('onboarding_completed') === 'true'

  if (location.pathname === '/setup-profile' && isAuthenticated) {
    const profileSetupCompleted = localStorage.getItem('profileSetupCompleted') === 'true'

    if (profileSetupCompleted) {
      return onboardingCompleted ? <MainTransition /> : <OnboardingTransition />
    }

    // Kein Überspringen, weil `user_metadata.full_name` gefüllt ist. Nach einer
    // Anmeldung mit Google oder Apple ist es das immer — Supabase legt es beim
    // Anlegen aus dem Anbieter-Namen an. Dieser Schritt ist aber der einzige
    // Ort, an dem ein Spitzname UND ein Profilbild gewählt werden; der volle
    // Klarname vom Anbieter ist ausdrücklich nicht die gewünschte Antwort.
    //
    // Bis 20.09.2026 entschied hier ein Wettlauf: `user` aus `useAuth()` war
    // beim Rendern meist noch leer, die Weiche griff deshalb selten. Auf einer
    // schnelleren Maschine wäre der Schritt übersprungen worden.

    if (loginMethod === 'password') {
      localStorage.setItem('profileSetupCompleted', 'true')
      return onboardingCompleted ? <MainTransition /> : <OnboardingTransition />
    }
  }

  if (location.pathname === '/home' && !onboardingCompleted) {
    if (isOfflineMode && offlineSetupCompleted) {
      // Offline user with completed setup can access home
    } else {
      return <OnboardingTransition />
    }
  }

  /*
    Der Logo-Schirm bleibt liegen, bis der Inhalt WIRKLICH steht.

    Bis hierher galt er nur, solange die ANMELDUNG lud (der Zweig weiter oben).
    Was danach kommt — Tabs wiederherstellen, das Projekt aus Dexie holen, den
    gemerkten Canvas-Zustand anwenden — lief vor den Augen des Benutzers ab:
    Die Aufgabenliste stand kurz mit offener Seitenleiste da, dann schnitt es
    auf den maximierten Canvas um.

    ÜBERLAGERN und nicht ersetzen, und das ist die tragende Entscheidung: Gäbe
    dieser Zweig den Schirm STATT der Kinder zurück, mountete `ProjectPage`
    nie, lüde nie, gäbe nie frei — der Aufschub liefe in seine Obergrenze und
    wir warteten eine Sekunde für nichts. Die Seite muss dahinter laden.

    Wer aufschiebt, steht in `useWindowRevealHold`. Heute genau einer.
  */
  if (!curtain) return <>{children}</>

  return (
    <>
      <RevealHandoff />
      {children}
      {revealHolding && <LogoScreen variant="full" />}
    </>
  )
}
